import React, { useState, useEffect } from 'react'
import { Link, useNavigate } from 'react-router-dom'
import { Users, Calendar, Package, BarChart3, Bell, Plus, Video, MapPin, MessageSquare, MessageCircle, TrendingUp, Settings, ArrowRight } from 'lucide-react'
import { useAdminAuth } from '../../contexts/AdminAuthContext'
import { useAuth } from '../../contexts/AuthContext'
import {
  countClients,
  countConsultationRequests,
  fetchAllClientMessageThreads,
  fetchAllPreferences,
  fetchCheckInRequests,
  fetchSessions,
  fetchUserPackages,
} from '../../lib/instantData'
import { isAdminEmail } from '../../lib/adminEmails'
import { db } from '../../lib/db'
import GlassCard from '../../components/ui/GlassCard'
import AnimatedCounter from '../../components/ui/AnimatedCounter'
import PanelShell from '../../components/ui/PanelShell'
import GradientButton from '../../components/ui/GradientButton'


interface DashboardStats {
  totalClients: number
  activePackages: number
  upcomingSessions: number
  consultationRequests: number
  newCheckInRequests: number
  unreadMessages: number
}
interface DashboardCard {
  title: string
  value: number
  icon: any
  prefix?: string
}

interface UpcomingSession {
  id: string
  client_name: string
  client_email: string
  session_type: 'in_person' | 'online' | 'meal_plan'
  scheduled_date: string
  status: string
}

const AdminDashboard: React.FC = () => {
  const { adminUser } = useAdminAuth()
  const { user } = useAuth()
  const { user: instantUser } = db.useAuth()
  const token = instantUser?.refresh_token
  const navigate = useNavigate()

    const [stats, setStats] = useState<DashboardStats>({
    totalClients: 0,
    activePackages: 0,
    upcomingSessions: 0,
    consultationRequests: 0,
    newCheckInRequests: 0,
    unreadMessages: 0,
  })
      const [loading, setLoading] = useState(true)
  const [upcomingSessions, setUpcomingSessions] = useState<UpcomingSession[]>([])

  useEffect(() => {
    fetchDashboardData()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token])

  const fetchDashboardData = async () => {
    try {
      const [totalClientsCount, consultationsCount, preferences, allSessions, userPackages, checkInRequests, messageThreads] =
        await Promise.all([
          countClients(),
          countConsultationRequests(),
          fetchAllPreferences(),
          fetchSessions(),
          fetchUserPackages(),
          fetchCheckInRequests(),
          token ? fetchAllClientMessageThreads(token) : Promise.resolve([]),
        ])

      setStats((prev) => ({
        ...prev,
        totalClients: totalClientsCount,
        consultationRequests: consultationsCount,
        newCheckInRequests: checkInRequests.filter((r: any) => r.status === 'new').length,
        unreadMessages: messageThreads.reduce((sum, t) => sum + t.unreadCount, 0),
      }))
      const clientMap = new Map<string, { name: string; email: string }>()
      preferences
        .filter((client) => !isAdminEmail(client.email))
        .forEach((client) => {
          clientMap.set(client.user_id, {
            name: client.full_name || 'Unknown Client',
            email: client.email || 'unknown@email.com',
          })
        })

      const scheduled = allSessions
        .filter((s) => s.status === 'scheduled')
        .sort((a, b) => a.scheduled_date.localeCompare(b.scheduled_date))

      setUpcomingSessions(
        scheduled.slice(0, 5).map((session) => {
          const clientInfo = clientMap.get(session.user_id) || {
            name: session.client_full_name || 'Unknown Client',
            email: session.client_email || 'unknown@email.com',
          }
          return {
            id: session.id,
            client_name: clientInfo.name,
            client_email: clientInfo.email,
            session_type: session.session_type,
            scheduled_date: session.scheduled_date,
            status: session.status,
          }
        })
      )

      setStats((prev) => ({
        ...prev,
        activePackages: userPackages.filter((p) => p.status === 'active').length,
      }))

      const now = Date.now()
      const nextWeek = now + 7 * 24 * 60 * 60 * 1000
      const upcomingCount = scheduled.filter((s) => {
        const t = Date.parse(s.scheduled_date)
        return t >= now && t <= nextWeek
      }).length
      setStats((prev) => ({ ...prev, upcomingSessions: upcomingCount }))
    } catch (error) {
      console.error('Error fetching dashboard data:', error)
    } finally {
      setLoading(false)
    }
  }

    const dashboardCards: DashboardCard[] = [
    { title: 'Total Clients', value: stats.totalClients, icon: Users },
      { title: 'Clients on Active Plans', value: stats.activePackages, icon: Package },
      { title: 'Upcoming Sessions', value: stats.upcomingSessions, icon: Calendar },
    { title: 'Consultations', value: stats.consultationRequests, icon: BarChart3 },
    { title: 'New Check-Ins', value: stats.newCheckInRequests, icon: MessageSquare },
    { title: 'Unread Messages', value: stats.unreadMessages, icon: MessageCircle },
  ]
    const quickActions = [
    { title: 'Manage Clients', icon: Users, href: '/admin/clients' },
    { title: 'Manage Sessions', icon: Calendar, href: '/admin/sessions' },
    { title: 'Consultations', icon: Calendar, href: '/admin/consultations' },
    { title: 'Check-In Requests', icon: MessageSquare, href: '/admin/check-in-requests' },
    { title: 'Client Messages', icon: MessageCircle, href: '/admin/client-messages' },
    { title: 'Weekly Check-Ins', icon: TrendingUp, href: '/admin/progress-checkins' },
    { title: 'Client Dashboards', icon: Users, href: '/admin/user-dashboards' },
    { title: 'Community Center', icon: MessageCircle, href: '/admin/community' },
    { title: 'Start Video Call', icon: Video, href: '/admin/video-call' },
    { title: 'Settings & Credentials', icon: Settings, href: '/admin/settings' },
  ]
  if (loading) {
    return (
      <div className="panel-page flex items-center justify-center">
        <div className="animate-spin rounded-full h-16 w-16 border-b-2 border-brand"></div>
      </div>
    )
  }

  const adminName = user?.user_metadata?.full_name || 'Admin'

  return (
    <PanelShell
      eyebrow="Admin"
      title="Dashboard"
      subtitle={`Welcome back, ${adminName}. Here's what's happening with your coaching business.`}
      actions={
        <GradientButton onClick={() => navigate('/admin/sessions')} size="sm" icon={Plus} variant="secondary">
          Manage sessions
        </GradientButton>
      }
    >
           <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-5 mb-8">
             {dashboardCards.map((card) => (
          <GlassCard key={card.title} hover={false}>
            <div className="flex items-start justify-between gap-3">
              <div>
                <p className="text-sm font-medium text-gray-500">{card.title}</p>
                <AnimatedCounter
                  end={card.value}
                  prefix={card.prefix || undefined}
                  className="mt-2 font-display text-3xl font-semibold text-ink"
                />
              </div>
              <div className="p-3">
                <card.icon className="h-5 w-5 text-brand" />
              </div>
            </div>
          </GlassCard>
        ))}
      </div>

      <GlassCard hover={false} className="mb-6 lg:mb-8">
        <div className="flex flex-col sm:flex-row sm:items-center gap-4 sm:gap-6">
          <div className="p-3 rounded-2xl bg-brand-soft shrink-0">
            <Users className="h-6 w-6 text-brand" />
          </div>
          <div className="flex-1">
            <h3 className="font-display text-xl font-semibold text-ink">Community Center</h3>
            <p className="text-sm text-gray-500 mt-0.5">
              Review posts and reports, manage members, and close out introduction requests.
            </p>
          </div>
          <GradientButton onClick={() => navigate('/admin/community')} icon={ArrowRight} size="sm" className="shrink-0">
            Manage Community Center
          </GradientButton>
        </div>
      </GlassCard>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 lg:gap-8">
        <GlassCard hover={false}>
          <h3 className="font-display text-xl font-semibold text-ink mb-4">Quick actions</h3>
          <div className="space-y-2">
            {quickActions.map((action) => (
              <Link
                key={action.title}
                to={action.href}
                className="flex items-center p-3 rounded-2xl border border-gray-100 hover:border-brand/30 hover:bg-brand-soft/60 transition-colors"
              >
                <div className="p-2">
                  <action.icon className="h-4 w-4 text-brand-blush" />
                </div>
                <span className="ml-3 text-sm font-semibold text-ink">{action.title}</span>
              </Link>
            ))}
          </div>
        </GlassCard>

        <div className="lg:col-span-2">
          <GlassCard hover={false}>
            <div className="flex items-center justify-between mb-4">
              <h3 className="font-display text-xl font-semibold text-ink">Upcoming sessions</h3>
              <Link to="/admin/sessions" className="panel-link">
                View all
              </Link>
            </div>
            <div className="space-y-3">
              {upcomingSessions.length === 0 ? (
                <div className="text-center py-10">
                  <div className="w-14 h-14 mx-auto mb-3 flex items-center justify-center">
                    <Calendar className="h-7 w-7 text-brand" />
                  </div>
                  <p className="text-gray-500 text-sm">No upcoming sessions</p>
                </div>
              ) : (
                upcomingSessions.map((session) => (
                  <div
                    key={session.id}
                    className="flex items-center justify-between p-4 rounded-2xl bg-cream border border-gray-100"
                  >
                    <div className="flex items-center">
                      <div className="w-10 h-10 flex items-center justify-center">
                        {session.session_type === 'online' ? (
                          <Video className="h-5 w-5 text-brand" />
                        ) : session.session_type === 'in_person' ? (
                          <MapPin className="h-5 w-5 text-brand" />
                        ) : (
                          <Package className="h-5 w-5 text-brand" />
                        )}
                      </div>
                      <div className="ml-3">
                        <p className="text-base font-semibold text-ink">{session.client_name}</p>
                        <p className="text-sm text-gray-600">
                          {session.session_type === 'online'
                            ? 'Online Training'
                            : session.session_type === 'in_person'
                              ? 'Private Training'
                              : 'Meal Plan Session'}
                        </p>
                      </div>
                    </div>
                    <div className="text-right">
                      <p className="text-sm text-gray-600">
                        {new Date(session.scheduled_date).toLocaleDateString()}
                      </p>
                      <p className="text-sm text-gray-600">
                        {new Date(session.scheduled_date).toLocaleTimeString([], {
                          hour: '2-digit',
                          minute: '2-digit',
                        })}
                      </p>
                      <span className="panel-chip-brand mt-1">Scheduled</span>
                    </div>
                  </div>
                ))
              )}
            </div>
          </GlassCard>
        </div>
      </div>

      <div className="mt-8">
        <GlassCard hover={false}>
          <div className="flex items-center mb-4">
            <Bell className="h-5 w-5 text-brand mr-2" />
            <h3 className="font-display text-xl font-semibold text-ink">Recent activity</h3>
          </div>
          <div className="space-y-3">
            <div className="flex items-center p-3 bg-cream rounded-2xl border border-gray-100">
              <div className="h-2 w-2 bg-brand rounded-full mr-3"></div>
              <p className="text-sm text-ink">New client registration: Sarah Johnson</p>
              <span className="ml-auto text-xs text-gray-500">2 hours ago</span>
            </div>
            <div className="flex items-center p-3 bg-cream rounded-2xl border border-gray-100">
              <div className="h-2 w-2 bg-brand rounded-full mr-3"></div>
              <p className="text-sm text-ink">Session completed: Online Training with Mike</p>
              <span className="ml-auto text-xs text-gray-500">4 hours ago</span>
            </div>
            <div className="flex items-center p-3 bg-cream rounded-2xl border border-gray-100">
              <div className="h-2 w-2 bg-brand rounded-full mr-3"></div>
              <p className="text-sm text-ink">Package expiring soon: Emily&apos;s 1-month plan</p>
              <span className="ml-auto text-xs text-gray-500">1 day ago</span>
            </div>
          </div>
        </GlassCard>
      </div>
    </PanelShell>
  )
}

export default AdminDashboard 
